// What the pages say of an agent's question around its card: whom it is put
// to, its words in one line, when it was asked, and the record of its answer
// that the session's transcript keeps where the answer came in.
//
// Only the entries the server marks `system` count: add_record can write
// entries of the same kind, body and references, and those prove nothing
// (Poulpe-408). Entries written before the mark prove nothing either.
//
// When it was asked is its first marked entry in its issue's record: the
// server writes one as it is asked, and another at each revision or
// reopening. A question on no issue leaves no record, and its time is not
// known. Its id says nothing: the asker may choose it.
//
// A question asked before the mark has unmarked entries before its first
// marked one, in the same issue's record. Its marked entries are then not
// its whole history: their first is not its asking, and their count is not
// its version, so neither is known. An unmarked entry only ever withholds.
//
// Its answer comes from the held Question, or, after delivery is confirmed,
// from the server-sent prompt that carried it. That prompt names each
// question and reply as JSON strings (board/questions.rs, `answer_prompt`).
// Who answered, when, and the version answered then come from the marked
// answer entry before the prompt, when there is one.
import { useMemo } from 'react'
import type { Actor, Question, RecordEntry } from '../api/types'
import { useBoardState } from '../store/context'
import { replyText } from './QuestionCard'
import { permissionText } from './permissionText'

/** Whether the question is put to `member`, the person the pages act as. */
export function putTo(question: Pick<Question, 'target'>, member: string | null | undefined): boolean {
  return !!member && question.target.kind === 'person' && question.target.member === member
}

/** A question in one line: its first prompt's words. */
export function askTitle(question: Pick<Question, 'prompts'>): string {
  return question.prompts[0] ? permissionText(question.prompts[0]).title : ''
}

/** A question's entries the server marked, oldest first, and whether they are its whole history. */
export interface QuestionHistory {
  entries: RecordEntry[]
  /** False when an unmarked `question` entry comes before the first marked one in its issue's record: the question predates the mark. */
  whole: boolean
}

/** Each question's history, from the entries the server marked in its issues' records, by the question's id. */
export function questionEntries(entries: ReadonlyMap<string, RecordEntry>): Map<string, QuestionHistory> {
  const marked = new Map<string, RecordEntry[]>()
  const unmarked = new Map<string, RecordEntry[]>()
  for (const entry of entries.values()) {
    if (!entry.system && entry.kind !== 'question') continue
    const into = entry.system ? marked : unmarked
    for (const ref of entry.refs ?? []) {
      if (ref.kind !== 'question') continue
      const list = into.get(ref.id)
      if (list) list.push(entry)
      else into.set(ref.id, [entry])
    }
  }
  const order = (a: RecordEntry, b: RecordEntry) => a.issue.localeCompare(b.issue) || a.number - b.number
  const byQuestion = new Map<string, QuestionHistory>()
  for (const [id, list] of marked) {
    list.sort(order)
    const first = list[0]!
    const earlier = (unmarked.get(id) ?? []).some((entry) => entry.issue === first.issue && entry.number < first.number)
    byQuestion.set(id, { entries: list, whole: !earlier })
  }
  return byQuestion
}

/** Each question's history, from the board's records, read again only when they change. */
export function useQuestionEntries(): Map<string, QuestionHistory> {
  const { entries } = useBoardState()
  return useMemo(() => questionEntries(entries), [entries])
}

/** When the question was asked, from its marked entries; null when it has none, or they are not its whole history. */
export function askedAt(id: string, byQuestion: ReadonlyMap<string, QuestionHistory>): string | null {
  const history = byQuestion.get(id)
  if (!history?.whole) return null
  return history.entries.find((entry) => entry.kind === 'question')?.at ?? null
}

/**
 * An RFC 3339 time as milliseconds since the epoch and the nanoseconds past
 * them: the server's times carry microseconds, which Date.parse drops.
 */
function instant(at: string): [number, number] {
  const parts = /^(.*T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(.*)$/.exec(at)
  if (!parts) return [Date.parse(at), 0]
  const digits = (parts[2] ?? '').padEnd(9, '0').slice(0, 9)
  return [Date.parse(`${parts[1]}.${digits.slice(0, 3)}${parts[3]}`), Number(digits.slice(3))]
}

/** Whether time `a` is at or before time `b`, to the nanosecond; false when either is not a time. */
export function atOrBefore(a: string, b: string): boolean {
  const [aMs, aNs] = instant(a)
  const [bMs, bNs] = instant(b)
  return aMs < bMs || (aMs === bMs && aNs <= bNs)
}

/** A prompt and the reply it was given, in words. */
export interface AnsweredPart {
  text: string
  answer: string
}

/** What the prompt carrying an answer says: who answered, as the server names them, and each prompt with its reply. */
export function readAnswerPrompt(text: string): { who: string; parts: AnsweredPart[] } | null {
  const lines = text.split('\n')
  const head = /^(.+) answered your question \S+\.$/.exec(lines[0] ?? '')
  if (!head) return null
  const json = (line: string | undefined, label: string): string | null => {
    if (!line?.startsWith(label)) return null
    try {
      const value: unknown = JSON.parse(line.slice(label.length))
      return typeof value === 'string' ? value : null
    } catch {
      return null
    }
  }
  const parts: AnsweredPart[] = []
  for (let i = 1; i < lines.length; i++) {
    const asked = json(lines[i], 'Question (agent-authored JSON): ')
    if (asked === null) continue
    const answer = json(lines[i + 1], "Answer (answerer's JSON): ")
    if (answer === null) return null
    parts.push({ text: asked, answer })
    i += 1
  }
  return parts.length > 0 ? { who: head[1]!, parts } : null
}

/** An answered question, as the transcript keeps it: each prompt with its reply, who answered and when, and the version answered when known. */
export interface Answered {
  parts: AnsweredPart[]
  /** Who answered, when the board says. */
  by: Actor | null
  /** Their name, as the prompt gives it. */
  who: string | null
  at: string
  version: number | null
  /** Whom it was put to, while the board holds the question. */
  target: Question['target'] | null
}

/** An answered question in a session's transcript, with the name of the agent that asked it. */
export type AskRecord = Answered & { asker: string }

/**
 * Who answered, when, and the version answered, from the last marked answer
 * entry at or before `at`: the version is how many times the question was
 * asked, revised or reopened before it, known only from its whole history.
 * Null without one.
 */
function markedAnswer(history: QuestionHistory | undefined, at: string): Pick<Answered, 'by' | 'at' | 'version'> | null {
  let found: Pick<Answered, 'by' | 'at' | 'version'> | null = null
  let version = 0
  const whole = history?.whole ?? false
  for (const entry of history?.entries ?? []) {
    if (entry.kind === 'question') version += 1
    else if (entry.kind === 'answer' && atOrBefore(entry.at, at)) found = { by: entry.author, at: entry.at, version: whole ? version : null }
  }
  return found
}

/**
 * The answer a prompt carried to question `id`: from the question while the
 * board holds it, else from the server-sent prompt's words and the
 * question's marked entries (`byQuestion`). Without a marked answer entry,
 * `at` is the prompt's own time once the board no longer holds it, and who
 * answered and the version are not known. Null when neither says what was
 * answered.
 */
export function answeredOf(
  id: string,
  prompt: { text: string; at: string },
  questions: ReadonlyMap<string, Question>,
  byQuestion: ReadonlyMap<string, QuestionHistory>,
): Answered | null {
  const question = questions.get(id)
  const answer = question?.answer
  if (question && answer) {
    const parts = question.prompts.map((p, i) => ({ text: p.text, answer: replyText(p, answer.replies[i]) }))
    return { parts, by: answer.by, who: readAnswerPrompt(prompt.text)?.who ?? null, at: answer.at, version: answer.version, target: question.target }
  }
  const read = readAnswerPrompt(prompt.text)
  if (!read) return null
  const marked = markedAnswer(byQuestion.get(id), prompt.at)
  return { parts: read.parts, by: null, at: prompt.at, version: null, ...marked, who: read.who, target: null }
}
